import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const overheatingCauses = [
    {
        id: 'cause-1',
        title: '1. Low Coolant Level',
        text: 'Coolant carries heat away from the engine. If the level drops too low, the system cannot control engine temperature effectively and overheating can happen fast.'
    },
    {
        id: 'cause-2',
        title: '2. Coolant Leak',
        text: 'A coolant leak can happen from hoses, the radiator, water pump, reservoir, or other cooling system components. Even a small leak can gradually turn into an overheating problem.'
    },
    {
        id: 'cause-3',
        title: '3. Thermostat Stuck Closed',
        text: 'The thermostat controls coolant flow between the engine and radiator. If it gets stuck closed, coolant circulation is restricted and the engine can overheat quickly.'
    },
    {
        id: 'cause-4',
        title: '4. Faulty Radiator Fan',
        text: 'The radiator fan helps cool the radiator when airflow is low, especially in traffic or while idling. If the fan motor, relay, or fuse fails, the car may overheat when stopped but seem better once moving.'
    },
    {
        id: 'cause-5',
        title: '5. Clogged Radiator',
        text: 'A blocked or internally restricted radiator cannot release heat effectively. Dirt, debris, or internal buildup can reduce cooling performance and push engine temperatures higher.'
    },
    {
        id: 'cause-6',
        title: '6. Water Pump Problem',
        text: 'The water pump moves coolant through the engine and radiator. If it starts failing, coolant circulation weakens and overheating can follow, sometimes with noise or coolant seepage near the pump.'
    },
    {
        id: 'cause-7',
        title: '7. Bad Radiator Cap',
        text: 'The radiator cap helps maintain proper system pressure. If it cannot hold pressure, coolant may boil too early and the system may lose efficiency, especially under heat and load.'
    },
    {
        id: 'cause-8',
        title: '8. Head Gasket Trouble',
        text: 'In more serious cases, overheating may be related to a failing head gasket. That can lead to coolant loss, combustion gases in the cooling system, white exhaust smoke, or repeated overheating that keeps coming back.'
    }
];

const faqs = [
    {
        question: 'Can I keep driving if my car is overheating?',
        answer: 'If the temperature is clearly too high or the gauge is in the red, it is safer to stop driving as soon as possible. Continued driving can cause serious engine damage.'
    },
    {
        question: 'Why does my car overheat only in traffic?',
        answer: 'That often points to a radiator fan issue, restricted airflow, or cooling system performance that drops when the car is not moving fast enough to get natural airflow.'
    },
    {
        question: 'Can low coolant cause overheating right away?',
        answer: 'Yes. If coolant is too low, the engine may lose the ability to control temperature properly and can overheat quickly.'
    },
    {
        question: 'Can CarFido Assistant help me decide what to do next?',
        answer: 'Yes. CarFido Assistant can help you decide whether the situation looks urgent, guide you through likely causes, and help you log the event in your vehicle history.'
    }
];

const Blog12 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 12;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            Why Is My Car Overheating? Top Causes and How to Fix It
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            A rising temperature gauge is never something to ignore. Car overheating can quickly turn into a serious engine problem, but the cause is often traceable if you catch it early.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated March 2026 &nbsp;|&nbsp; 9 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/12_1.jpeg"
                            alt="Driver noticing an overheating engine warning"
                            title="Why Is My Car Overheating?"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: engine_overheating_scene.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#overheating-summary" className="text-gray-900 no-underline hover:underline">Overheating summary</a></li>
                            <li><a href="#why-it-matters" className="text-gray-900 no-underline hover:underline">Why overheating matters</a></li>
                            <li><a href="#cause-1" className="text-gray-900 no-underline hover:underline">Low coolant</a></li>
                            <li><a href="#cause-2" className="text-gray-900 no-underline hover:underline">Coolant leak</a></li>
                            <li><a href="#cause-3" className="text-gray-900 no-underline hover:underline">Thermostat</a></li>
                            <li><a href="#cause-4" className="text-gray-900 no-underline hover:underline">Radiator fan</a></li>
                            <li><a href="#cause-5" className="text-gray-900 no-underline hover:underline">Clogged radiator</a></li>
                            <li><a href="#cause-6" className="text-gray-900 no-underline hover:underline">Water pump</a></li>
                            <li><a href="#cause-7" className="text-gray-900 no-underline hover:underline">Radiator cap</a></li>
                            <li><a href="#cause-8" className="text-gray-900 no-underline hover:underline">Head gasket</a></li>
                            <li><a href="#what-to-do-now" className="text-gray-900 no-underline hover:underline">What to do right away</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            If your temperature gauge is climbing, steam is coming from under the hood, or an overheating warning appears on the dashboard, the engine is running hotter than it should. In most cases, that means the cooling system is no longer moving heat away effectively.
                        </p>
                        <p className="mb-[18px] text-gray-700">
                            Sometimes overheating happens only in traffic. Other times it happens during highway driving, after a long climb, or suddenly with very little warning. The exact pattern often gives you a useful clue about the real cause.
                        </p>
                        <p className="mb-0 text-gray-700">
                            This guide covers the most common reasons cars overheat, what each one can lead to, and what is worth doing before the situation turns into serious engine damage.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>Why is my car overheating?</strong><br />
                            Common causes include low coolant, coolant leaks, a stuck thermostat, radiator fan failure, clogged radiator, bad water pump, faulty radiator cap, or more serious problems like a head gasket issue.
                        </p>
                    </section>

                    <section id="overheating-summary" className="mb-10">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Car Overheating Summary</h2>
                        <div className="overflow-x-auto border border-gray-200 rounded-xl">
                            <table className="w-full border-collapse bg-white text-sm">
                                <thead className="bg-gray-50 text-gray-900 font-bold">
                                    <tr>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Possible Cause</th>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">What It Can Lead To</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Low coolant</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Not enough cooling capacity to control engine temperature</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Coolant leak</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Rapid coolant loss and repeated overheating</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Thermostat stuck closed</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Coolant cannot circulate properly through the engine</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Radiator fan problem</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Overheating especially at idle or in traffic</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Clogged radiator</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Poor heat transfer and rising coolant temperature</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Water pump failure</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Weak or no coolant circulation</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Bad radiator cap</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Pressure loss and lower boiling point for coolant</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 font-medium text-gray-900">Head gasket issue</td>
                                        <td className="py-3.5 px-4 text-gray-600">Internal engine damage risk and severe overheating</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="why-it-matters" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Why Overheating Matters</h2>
                        <p className="mb-4 text-gray-700">
                            An overheating engine is not just a comfort or warning-light issue. Too much heat can damage engine components quickly, especially if you keep driving after the temperature goes too high.
                        </p>
                        <p className="mb-0 text-gray-700">
                            That is why the best first step is to decide whether you should stop driving immediately or whether the situation appears mild enough to move the car safely to a better location.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/12_2.jpeg"
                            alt="Car overheating system components diagram"
                            title="Cooling system parts linked to overheating"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: car_overheating_system_components.png
                        </figcaption>
                    </figure>

                    {overheatingCauses.map((cause) => (
                        <section key={cause.id} id={cause.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {cause.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{cause.text}</p>
                        </section>
                    ))}

                    <section id="what-to-do-now" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            What to Do Right Away
                        </h2>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>If the gauge is in the red, stop driving as soon as it is safe</li>
                            <li>Turn off the AC and use the heater if you must move briefly to a safer place</li>
                            <li>Do not open the radiator cap while the engine is hot</li>
                            <li>Let the engine cool before checking coolant level</li>
                            <li>Look for obvious leaks, steam, or fan problems once it is safe to inspect</li>
                        </ul>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                How CarFido Assistant Helps
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                If your car is overheating, CarFido Assistant can help you decide what to do first and what the likely cause may be before the problem gets worse.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido Assistant helps you:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Give immediate action steps, including when to stop driving versus continue briefly</li>
                                <li>Identify the likely cause through guided diagnosis</li>
                                <li>Recommend next steps in a practical work plan</li>
                                <li>Track overheating incidents in your vehicle history</li>
                            </ul>
                            <p className="mt-4 mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">Drivers can ask questions such as:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>My temperature gauge is rising in traffic. Should I stop now?</li>
                                <li>Could low coolant alone cause this overheating?</li>
                                <li>What should I inspect after the engine cools down?</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            Car overheating is something to take seriously, but the root cause is often traceable if you pay attention to when it happens and how the car behaves.
                        </p>
                        <p className="mb-4 text-gray-700">
                            Whether the issue is low coolant, a stuck thermostat, a weak fan, or something more serious, acting early can save you from much larger repairs later.
                        </p>
                        <p className="mb-0 text-gray-700">
                            If your engine temperature keeps rising, it is better not to wait and hope it settles down on its own. A quick response can protect the engine and make diagnosis much easier.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#overheating-summary" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Overheating summary</a></li>
                            <li><a href="#why-it-matters" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Why it matters</a></li>
                            <li><a href="#cause-1" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Low coolant</a></li>
                            <li><a href="#cause-3" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Thermostat</a></li>
                            <li><a href="#cause-4" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Radiator fan</a></li>
                            <li><a href="#what-to-do-now" className="no-underline text-gray-600 hover:text-gray-900 text-sm">What to do now</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog12;
